import { useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { XIcon, LinkIcon, ClipboardIcon, SparkleIcon } from "@phosphor-icons/react";

export default function ImportCarGrModal({ onClose, onImported }) {
  const [tab, setTab] = useState("paste"); // 'url' or 'paste'
  const [url, setUrl] = useState("");
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);

  const doImport = async () => {
    if (tab === "url" && !url.trim()) return;
    if (tab === "paste" && !text.trim()) return;
    setLoading(true);
    try {
      const payload = tab === "url" ? { url: url.trim() } : { url: url.trim(), text };
      const r = await api.post("/import/cargr", payload);
      toast.success("Δεδομένα εισήχθησαν");
      onImported(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Αποτυχία εισαγωγής";
      toast.error(typeof msg === "string" ? msg : "Σφάλμα");
      // If URL failed, switch to paste tab automatically
      if (tab === "url" && /car\.gr/i.test(typeof msg === "string" ? msg : "")) {
        setTab("paste");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white border border-black/10 w-full max-w-2xl max-h-[90vh] overflow-auto" data-testid="import-modal">
        <div className="px-5 py-3 border-b border-black/10 bg-[#0A0A0A] text-white flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SparkleIcon size={18} weight="fill" color="#F59E0B" />
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-white/60">Εισαγωγή από</div>
              <h3 className="heading font-bold">car.gr (AI παραγωγή)</h3>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/10"><XIcon size={18} /></button>
        </div>

        <div className="flex border-b border-black/10">
          <button
            onClick={() => setTab("paste")}
            data-testid="tab-paste"
            className={`flex-1 px-4 py-3 text-sm flex items-center justify-center gap-2 ${tab === "paste" ? "bg-[#0A0A0A] text-white" : "hover:bg-[#F9FAFB]"}`}
          >
            <ClipboardIcon size={16} /> Επικόλληση περιεχομένου
          </button>
          <button
            onClick={() => setTab("url")}
            data-testid="tab-url"
            className={`flex-1 px-4 py-3 text-sm flex items-center justify-center gap-2 ${tab === "url" ? "bg-[#0A0A0A] text-white" : "hover:bg-[#F9FAFB]"}`}
          >
            <LinkIcon size={16} /> Μόνο URL
          </button>
        </div>

        <div className="p-5 space-y-4">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-[0.15em] text-[#4B5563]">URL car.gr (προαιρετικό για αναφορά)</span>
            <input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://besikoscars.car.gr/cars/view/53791157"
              data-testid="import-url"
              className="px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm font-mono"
            />
          </label>

          {tab === "paste" ? (
            <>
              <div className="bg-[#F9FAFB] border border-black/10 p-3 text-xs text-[#4B5563]">
                <strong>Οδηγίες</strong>: Ανοίξτε τη σελίδα του αυτοκινήτου στο car.gr.
                Πατήστε <kbd className="bg-white px-1.5 py-0.5 border border-black/20">Ctrl/Cmd + A</kbd> για να επιλέξετε όλο το περιεχόμενο,
                μετά <kbd className="bg-white px-1.5 py-0.5 border border-black/20">Ctrl/Cmd + C</kbd> για αντιγραφή,
                και επικολλήστε εδώ. Το AI θα εξάγει αυτόματα τα στοιχεία.
              </div>
              <label className="flex flex-col gap-1">
                <span className="text-[10px] uppercase tracking-[0.15em] text-[#4B5563]">Περιεχόμενο σελίδας</span>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={10}
                  placeholder="Επικολλήστε εδώ ολόκληρο το κείμενο από τη σελίδα του car.gr..."
                  data-testid="import-text"
                  className="px-3 py-2 border border-black/20 outline-none focus:border-[#0055FF] text-sm font-mono"
                />
              </label>
            </>
          ) : (
            <div className="bg-[#FEF3C7] border border-[#F59E0B] p-3 text-xs text-[#92400E]">
              ⚠️ Το car.gr αποκλείει αυτοματοποιημένες επισκέψεις από διακομιστές (Cloudflare). Αυτή η μέθοδος συνήθως αποτυγχάνει.
              Συνιστάται η <strong>«Επικόλληση περιεχομένου»</strong> για αξιόπιστα αποτελέσματα.
            </div>
          )}
        </div>

        <div className="px-5 py-3 border-t border-black/10 bg-[#F9FAFB] flex justify-end gap-2">
          <button onClick={onClose} className="btn-secondary">Άκυρο</button>
          <button
            onClick={doImport}
            disabled={loading || (tab === "url" ? !url.trim() : !text.trim())}
            className="btn-accent flex items-center gap-1.5"
            data-testid="import-submit"
          >
            <SparkleIcon size={16} weight="fill" />
            {loading ? "Επεξεργασία AI..." : "Εισαγωγή"}
          </button>
        </div>
      </div>
    </div>
  );
}
